import React, { useRef, useEffect } from 'react';
import { Box, Typography, Avatar, keyframes } from '@mui/material';

interface Transcript {
  source: string;
  message: string;
  timestamp: Date;
}

interface ModernConversationAreaProps {
  transcript: Transcript[];
  isSpeaking: boolean;
  agentInfo?: any;
}

// Keyframes for speaking animation
const bounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  40% {
    transform: translateY(-8px);
    opacity: 1;
  }
`;

export const ModernConversationArea: React.FC<ModernConversationAreaProps> = ({
  transcript,
  isSpeaking,
  agentInfo,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript, isSpeaking]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
      }}
    >
      {/* Messages Container */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          py: 2,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: '#E0E0E0',
            borderRadius: '3px',
            '&:hover': {
              bgcolor: '#BDBDBD',
            },
          },
        }}
      >
        {transcript.map((item, index) => {
          // Handle system messages
          if (item.source === 'system') {
            return (
              <Box
                key={index}
                sx={{
                  textAlign: 'center',
                  my: 2,
                  px: 2,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontSize: '12px',
                    fontStyle: 'italic',
                    display: 'inline-block',
                    px: 2,
                    py: 0.5,
                    bgcolor: '#F0F0F0',
                    borderRadius: 10,
                  }}
                >
                  {item.message}
                </Typography>
              </Box>
            );
          }

          const isAgent = item.source === 'ai';

          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                mb: 3,
                flexDirection: isAgent ? 'row' : 'row-reverse',
              }}
            >
              {/* Avatar */}
              <Avatar
                src={isAgent ? agentInfo?.profile?.avatar : undefined}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: isAgent ? 'transparent' : '#E3F2FD',
                  color: isAgent ? undefined : '#1976D2',
                  fontSize: '16px',
                  fontWeight: 600,
                  mx: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                {!isAgent && 'C'}
              </Avatar>

              {/* Message Content */}
              <Box
                sx={{
                  maxWidth: '70%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isAgent ? 'flex-start' : 'flex-end',
                }}
              >
                {/* Speaker Label */}
                <Typography
                  variant="caption"
                  sx={{
                    color: '#333',
                    fontSize: '13px',
                    fontWeight: 500,
                    mb: 0.5,
                    px: 1,
                  }}
                >
                  {isAgent ? agentInfo?.profile?.name || 'Rachel' : 'Customer'}
                </Typography>

                {/* Message Bubble */}
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    bgcolor: isAgent ? 'white' : '#1976D2',
                    color: isAgent ? '#333' : 'white',
                    borderRadius: 2.5,
                    boxShadow: isAgent
                      ? '0 2px 8px rgba(0,0,0,0.08)'
                      : '0 2px 8px rgba(25,118,210,0.2)',
                    borderTopLeftRadius: isAgent ? 0 : undefined,
                    borderTopRightRadius: !isAgent ? 0 : undefined,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '15px',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {item.message}
                  </Typography>
                </Box>

                {/* Timestamp */}
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontSize: '12px',
                    mt: 0.5,
                    px: 1,
                  }}
                >
                  {item.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {/* Show speaking indicator when agent is speaking */}
        {isSpeaking && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              mb: 3,
              flexDirection: 'row',
            }}
          >
            {/* Avatar */}
            <Avatar
              src={agentInfo?.profile?.avatar}
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'transparent',
                mx: 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            />

            {/* Message Content */}
            <Box
              sx={{
                maxWidth: '70%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              {/* Speaker Label */}
              <Typography
                variant="caption"
                sx={{
                  color: '#333',
                  fontSize: '13px',
                  fontWeight: 500,
                  mb: 0.5,
                  px: 1,
                }}
              >
                {agentInfo?.profile?.name || 'Rachel'}
              </Typography>

              {/* Speaking Indicator Bubble */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  bgcolor: 'white',
                  borderRadius: 2.5,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  borderTopLeftRadius: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 60,
                }}
              >
                {[0, 1, 2].map((dotIndex) => (
                  <Box
                    key={dotIndex}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#1976D2',
                      animation: `${bounce} 1.4s infinite ease-in-out`,
                      animationDelay: `${dotIndex * 0.16}s`,
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>
    </Box>
  );
};
